import {
  useDeleteNote,
  useGetNote,
  useGetNotes,
  useUpdateNote,
  type Note,
  type NoteSortField,
  type NoteSortOrder,
} from "@/api/notes/notes";
import { NewNoteModal } from "@/components/notes/NewNoteModal";
import { EditNoteTitleModal } from "@/components/notes/EditNoteTitleModal";
import { NotesEditor } from "@/components/notes/NotesEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import { SearchIcon, Pencil, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const SORT_OPTIONS: { value: `${NoteSortField}-${NoteSortOrder}`; key: string }[] = [
  { value: "updatedAt-desc", key: "notes.sortUpdatedDesc" },
  { value: "updatedAt-asc", key: "notes.sortUpdatedAsc" },
  { value: "title-asc", key: "notes.sortTitleAsc" },
  { value: "title-desc", key: "notes.sortTitleDesc" },
];

function formatModifiedDate(dateStr: string | null): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function NotesDashboard() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<`${NoteSortField}-${NoteSortOrder}`>("updatedAt-desc");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newNoteOpen, setNewNoteOpen] = useState(false);
  const [editTitleNoteId, setEditTitleNoteId] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [pendingNoteId, setPendingNoteId] = useState<string | null>(null);
  const [editorDraft, setEditorDraft] = useState("");

  const [sortBy, sortOrder] = sort.split("-") as [NoteSortField, NoteSortOrder];
  const { data: notes = [], isLoading: notesLoading } = useGetNotes({
    search: search.trim() || undefined,
    sortBy,
    sortOrder,
  });
  const { data: selectedNote } = useGetNote(selectedId ?? undefined);
  const updateNote = useUpdateNote();
  const deleteNote = useDeleteNote();

  // Select first note by default when notes load
  useEffect(() => {
    if (!notesLoading && notes.length > 0 && selectedId === null) {
      setSelectedId(notes[0].id);
    }
  }, [notesLoading, notes, selectedId]);

  useEffect(() => {
    if (selectedNote && selectedId === selectedNote.id) {
      setEditorDraft(selectedNote.content ?? "");
    }
  }, [selectedId, selectedNote]);

  const effectiveContent =
    selectedNote && selectedId === selectedNote.id
      ? editorDraft
      : selectedNote?.content ?? "";

  const hasUnsavedChanges =
    !!selectedId &&
    !!selectedNote &&
    selectedId === selectedNote.id &&
    editorDraft !== (selectedNote.content ?? "");

  const handleSave = useCallback(() => {
    if (!selectedId) return;
    updateNote.mutate(
      { id: selectedId, content: editorDraft },
      {
        onSuccess: () => {
          toast({
            title: t("notes.save"),
            description: t("notes.save"),
            variant: "success",
            duration: 1000,
          });
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to save",
            variant: "error",
          });
        },
      },
    );
  }, [selectedId, editorDraft, updateNote, t]);

  const handleSaveAndSwitch = useCallback(
    (nextNoteId: string) => {
      if (!selectedId) return;
      updateNote.mutate(
        { id: selectedId, content: editorDraft },
        {
          onSuccess: () => {
            setSelectedId(nextNoteId);
            setPendingNoteId(null);
            toast({
              title: t("notes.save"),
              variant: "success",
              duration: 1000,
            });
          },
        },
      );
    },
    [selectedId, editorDraft, updateNote, t],
  );

  const handleContentChange = useCallback((html: string) => {
    setEditorDraft(html);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTargetId) return;
    const index = notes.findIndex((n) => n.id === deleteTargetId);
    deleteNote.mutate(deleteTargetId, {
      onSuccess: () => {
        if (selectedId === deleteTargetId) {
          if (notes.length <= 1) {
            setSelectedId(null);
          } else if (index > 0) {
            setSelectedId(notes[index - 1].id);
          } else {
            setSelectedId(notes[1].id);
          }
        }
        setDeleteTargetId(null);
        toast({
          title: t("notes.delete"),
          variant: "success",
          duration: 1000,
        });
      },
    });
  }, [deleteTargetId, selectedId, notes, deleteNote, t]);

  return (
    <div className="flex flex-col items-start gap-4">
      {/* Section 1: Header (same layout/size as Oeuvres, Writing, etc.) */}
      <div className="flex justify-between items-start w-full">
        <div className="flex flex-col gap-1">
          <p className="text-[32px] font-[600]">{t("notes.title")}</p>
          {selectedId && (
            <p className="text-xl font-semibold text-slate-800 truncate">
              {selectedNote?.title || t("notes.newNoteTitle")}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-3">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t("notes.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-[18px] min-w-[400px]"
            />
          </div>
          <div className="flex flex-row justify-between gap-2">
            <Select value={sort} onValueChange={(v) => setSort(v as typeof sort)}>
              <SelectTrigger className="min-w-[220px]">
                <SelectValue placeholder={t("notes.sortBy")} />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(opt.key)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => setNewNoteOpen(true)}>
              {t("notes.addNote")}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 w-full">
        {/* Section 2: Main area – editor */}
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          {selectedId && notes.some((n) => n.id === selectedId) ? (
            <NotesEditor
              key={selectedId}
              content={effectiveContent}
              onChange={handleContentChange}
              onSave={handleSave}
                disabled={updateNote.isPending}
              />
          ) : (
            <div className="flex-1 border border-dashed border-input rounded-lg bg-muted/30 flex items-center justify-center text-center px-6 mr-4">
              {notesLoading ? (
                <span className="text-muted-foreground">{t("notes.searchPlaceholder")}</span>
              ) : notes.length === 0 || (selectedId != null && !notes.some((n) => n.id === selectedId)) ? (
                <p className="text-muted-foreground max-w-md">{t("notes.noNotesMessage")}</p>
              ) : (
                <span className="text-muted-foreground">{t("notes.title")}</span>
              )}
            </div>
          )}
        </div>

        {/* Section 3: Right column – list (same visual as sidebar MenuItem) */}
        <div className="w-[260px] shrink-0 border-l border-slate-200 flex flex-col min-h-0">
          <div className="overflow-y-auto">
            {notes.map((note: Note) => {
              const isSelected = selectedId === note.id;
              return (
                <div
                  key={note.id}
                  className={`group flex items-center justify-between gap-2 min-h-12 px-4 py-3 border-l-4 cursor-pointer ${
                    isSelected
                      ? "border-primary bg-[#def2ff] text-primary"
                      : "border-transparent text-slate-700 hover:bg-slate-50"
                  }`}
                  onClick={() => {
                    if (note.id === selectedId) return;
                    if (hasUnsavedChanges) {
                      setPendingNoteId(note.id);
                    } else {
                      setSelectedId(note.id);
                    }
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <p
                      className={`text-sm truncate ${
                        isSelected ? "font-bold text-primary" : "font-medium text-slate-800"
                      }`}
                    >
                      {note.title || t("notes.newNoteTitle")}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t("notes.modifiedOn")} {formatModifiedDate(note.updatedAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-0 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="opacity-0 group-hover:opacity-100 h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditTitleNoteId(note.id);
                      }}
                      aria-label={t("notes.editTitle")}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                    variant="ghost"
                    size="icon"
                    className="opacity-0 group-hover:opacity-100 shrink-0 h-8 w-8 text-destructive hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTargetId(note.id);
                    }}
                    aria-label={t("notes.delete")}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <NewNoteModal
        open={newNoteOpen}
        onOpenChange={setNewNoteOpen}
        onCreated={(id) => setSelectedId(id)}
      />

      {editTitleNoteId && (
        <EditNoteTitleModal
          open={!!editTitleNoteId}
          onOpenChange={(open) => !open && setEditTitleNoteId(null)}
          noteId={editTitleNoteId}
          initialTitle={notes.find((n) => n.id === editTitleNoteId)?.title ?? ""}
          onUpdated={() => setEditTitleNoteId(null)}
        />
      )}

      <AlertDialog open={!!deleteTargetId} onOpenChange={(open) => !open && setDeleteTargetId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("notes.confirmDelete")}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("notes.actionCancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("notes.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingNoteId} onOpenChange={(open) => !open && setPendingNoteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("notes.unsavedChangesTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("notes.unsavedChangesMessage")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => setPendingNoteId(null)}>
              {t("notes.actionCancel")}
            </AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => {
                if (pendingNoteId) {
                  setSelectedId(pendingNoteId);
                  setPendingNoteId(null);
                }
              }}
            >
              {t("notes.discardChanges")}
            </Button>
            <Button
              onClick={() => pendingNoteId && handleSaveAndSwitch(pendingNoteId)}
              disabled={updateNote.isPending}
            >
              {t("notes.save")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
